import { createAction } from '@reduxjs/toolkit';

import type { CreateFlowPayload } from '@polyai/stream/commands/handlers/flow/create-flow/schema';
import type { CreateFlowStepPayload } from '@polyai/stream/commands/handlers/flow/create-flow-step/schema';
import type { CreateNoCodeConditionPayload } from '@polyai/stream/commands/handlers/flow/create-no-code-condition/schema';
import type { CreateNoCodeStepPayload } from '@polyai/stream/commands/handlers/flow/create-no-code-step/schema';
import type { CreateStepPayload } from '@polyai/stream/commands/handlers/flow/create-step/schema';
import type { CreateStepConditionPayload } from '@polyai/stream/commands/handlers/flow/create-step-condition/schema';
import type { CreateTransitionFunctionPayload } from '@polyai/stream/commands/handlers/flow/create-transition-function/schema';
import type { DeleteFlowPayload } from '@polyai/stream/commands/handlers/flow/delete-flow/schema';
import type { DeleteFlowStepPayload } from '@polyai/stream/commands/handlers/flow/delete-flow-step/schema';
import type { DeleteNoCodeConditionPayload } from '@polyai/stream/commands/handlers/flow/delete-no-code-condition/schema';
import type { DeleteNoCodeStepPayload } from '@polyai/stream/commands/handlers/flow/delete-no-code-step/schema';
import type { DeleteStepPayload } from '@polyai/stream/commands/handlers/flow/delete-step/schema';
import type { DeleteStepConditionPayload } from '@polyai/stream/commands/handlers/flow/delete-step-condition/schema';
import type { DeleteTransitionFunctionPayload } from '@polyai/stream/commands/handlers/flow/delete-transition-function/schema';
import type { ImportFlowPayload } from '@polyai/stream/commands/handlers/flow/import-flow/schema';
import type { MoveFlowComponentPayload } from '@polyai/stream/commands/handlers/flow/move-flow-component/schema';
import type { MoveFlowComponentsPayload } from '@polyai/stream/commands/handlers/flow/move-flow-components/schema';
import type { UpdateNoCodeConditionExitFlowPositionPayload } from '@polyai/stream/commands/handlers/flow/update-exit-flow-position/schema';
import type { UpdateFlowActionPayload } from '@polyai/stream/commands/handlers/flow/update-flow/schema';
import type { UpdateFlowStepPayload } from '@polyai/stream/commands/handlers/flow/update-flow-step/schema';
import type { UpdateNoCodeConditionPayload } from '@polyai/stream/commands/handlers/flow/update-no-code-condition/schema';
import type { UpdateNoCodeConditionPositionPayload } from '@polyai/stream/commands/handlers/flow/update-no-code-condition-position/schema';
import type { UpdateNoCodeStepPayload } from '@polyai/stream/commands/handlers/flow/update-no-code-step/schema';
import type { UpdateNoCodeStepPositionPayload } from '@polyai/stream/commands/handlers/flow/update-no-code-step-position/schema';
import type { UpdateStepPayload } from '@polyai/stream/commands/handlers/flow/update-step/schema';
import type { UpdateFlowStepAsrConfigPayload } from '@polyai/stream/commands/handlers/flow/update-step-asr/schema';
import type { UpdateStepConditionPayload } from '@polyai/stream/commands/handlers/flow/update-step-condition/schema';
import type { UpdateStepConditionPositionPayload } from '@polyai/stream/commands/handlers/flow/update-step-condition-position/schema';
import type { UpdateFlowStepDTMFConfigPayload } from '@polyai/stream/commands/handlers/flow/update-step-dtmf/schema';
import type { UpdateFlowStepPositionPayload } from '@polyai/stream/commands/handlers/flow/update-step-position/schema';
import type { UpdateFlowStepPositionsPayload } from '@polyai/stream/commands/handlers/flow/update-step-positions/schema';
import type { UpdateFlowTransitionFunctionPayload } from '@polyai/stream/commands/handlers/flow/update-transition-function/schema';
import type { UpdateFlowTransitionFunctionLatencyControlPayload } from '@polyai/stream/commands/handlers/flow/update-transition-function-latency-control/schema';

const PREFIX = 'flows';

// Flow actions
export const createFlow = createAction<CreateFlowPayload>(
  `${PREFIX}/createFlow`,
);

export const updateFlow = createAction<UpdateFlowActionPayload>(
  `${PREFIX}/updateFlow`,
);

export const deleteFlow = createAction<DeleteFlowPayload>(
  `${PREFIX}/deleteFlow`,
);

export const importFlow = createAction<ImportFlowPayload>(
  `${PREFIX}/importFlow`,
);

// Flow Step actions
export const createFlowStep = createAction<CreateFlowStepPayload>(
  `${PREFIX}/createFlowStep`,
);

export const updateFlowStep = createAction<UpdateFlowStepPayload>(
  `${PREFIX}/updateFlowStep`,
);

/** @deprecated Use `deleteStep` instead — it handles all step types including advanced steps. */
export const deleteFlowStep = createAction<DeleteFlowStepPayload>(
  `${PREFIX}/deleteFlowStep`,
);

export const updateStepPosition = createAction<UpdateFlowStepPositionPayload>(
  `${PREFIX}/updateFlowStepPosition`,
);

export const updateStepPositions = createAction<UpdateFlowStepPositionsPayload>(
  `${PREFIX}/updateFlowStepPositions`,
);

export const updateFlowStepDTMFConfig =
  createAction<UpdateFlowStepDTMFConfigPayload>(
    `${PREFIX}/updateFlowStepDTMFConfig`,
  );

export const updateFlowStepASRConfig =
  createAction<UpdateFlowStepAsrConfigPayload>(
    `${PREFIX}/updateFlowStepASRConfig`,
  );

// no code step
export const createNoCodeStep = createAction<CreateNoCodeStepPayload>(
  `${PREFIX}/createNoCodeStep`,
);

export const updateNoCodeStep = createAction<UpdateNoCodeStepPayload>(
  `${PREFIX}/updateNoCodeStep`,
);

/** @deprecated Use `deleteStep` instead. Kept for backwards compatibility with auto-generated protobuf/mapper files. */
export const deleteNoCodeStep = createAction<DeleteNoCodeStepPayload>(
  `${PREFIX}/deleteNoCodeStep`,
);
export const updateNoCodeConditionPosition =
  createAction<UpdateNoCodeConditionPositionPayload>(
    `${PREFIX}/updateNoCodeConditionPosition`,
  );

// no code step condition
export const createNoCodeCondition = createAction<CreateNoCodeConditionPayload>(
  `${PREFIX}/createNoCodeCondition`,
);

export const updateNoCodeCondition = createAction<UpdateNoCodeConditionPayload>(
  `${PREFIX}/updateNoCodeCondition`,
);

export const deleteNoCodeCondition = createAction<DeleteNoCodeConditionPayload>(
  `${PREFIX}/deleteNoCodeCondition`,
);

export const updateNoCodeStepPosition =
  createAction<UpdateNoCodeStepPositionPayload>(
    `${PREFIX}/updateNoCodeStepPosition`,
  );

// no code step exit flow
export const updateNoCodeConditionExitFlowPosition =
  createAction<UpdateNoCodeConditionExitFlowPositionPayload>(
    `${PREFIX}/updateNoCodeConditionExitFlowPosition`,
  );

// Flow Transition Function actions
export const createFlowTransitionFunction =
  createAction<CreateTransitionFunctionPayload>(
    `${PREFIX}/createFlowTransitionFunction`,
  );

export const updateFlowTransitionFunction =
  createAction<UpdateFlowTransitionFunctionPayload>(
    `${PREFIX}/updateFlowTransitionFunction`,
  );

export const deleteFlowTransitionFunction =
  createAction<DeleteTransitionFunctionPayload>(
    `${PREFIX}/deleteFlowTransitionFunction`,
  );

// Flow Transition Function Latency Control actions
export const updateFlowTransitionFunctionLatencyControl =
  createAction<UpdateFlowTransitionFunctionLatencyControlPayload>(
    `${PREFIX}/updateFlowTransitionFunctionLatencyControl`,
  );

export const moveFlowComponent = createAction<MoveFlowComponentPayload>(
  `${PREFIX}/moveFlowComponent`,
);

export const moveFlowComponents = createAction<MoveFlowComponentsPayload>(
  `${PREFIX}/moveFlowComponents`,
);

// Generic Step actions (discriminated union, extensible for all step types)
export const createStep = createAction<CreateStepPayload>(
  `${PREFIX}/createStep`,
);

export const updateStep = createAction<UpdateStepPayload>(
  `${PREFIX}/updateStep`,
);

export const deleteStep = createAction<DeleteStepPayload>(
  `${PREFIX}/deleteStep`,
);

export const createStepCondition = createAction<CreateStepConditionPayload>(
  `${PREFIX}/createStepCondition`,
);

export const updateStepCondition = createAction<UpdateStepConditionPayload>(
  `${PREFIX}/updateStepCondition`,
);

export const deleteStepCondition = createAction<DeleteStepConditionPayload>(
  `${PREFIX}/deleteStepCondition`,
);

export const updateStepConditionPosition =
  createAction<UpdateStepConditionPositionPayload>(
    `${PREFIX}/updateStepConditionPosition`,
  );
